import React from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useBottomRightCornerActions,
	useBottomRightCornerContainer,
} from '../../../controllers/layout-controller/consumers/bottom-right-corner-controller/index.tsx';
import { useActiveRightSidebarState } from '../../../controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useDetailPanelWidth } from '../../../controllers/layout-controller/store/index.tsx';
import type { ProviderProps, GlobalBottomRightCornerContainerProps } from './types.tsx';
/**
 * Adjusts a container's position dynamically in relation to the state of a right sidebar and given offset parameters. This ensures the container is always correctly positioned, taking into account the sidebar's visibility and size.
 */
export function ContainerWithRef({
	bottomOffset,
	rightOffset,
}: Required<GlobalBottomRightCornerContainerProps>) {
	const [{ width }] = useDetailPanelWidth();
	const rightSidebar = useActiveRightSidebarState();
	const { registerBottomRightCornerContainer } = useBottomRightCornerActions();

	const isSidebarOpen = rightSidebar && !rightSidebar.isCollapsed && !rightSidebar.isMinimised;
	const rightSidebarWidth = isSidebarOpen ? rightSidebar.width : 0;

	const rightSidebarOffset = isSidebarOpen ? rightSidebarWidth : 0;
	let distanceFromRightSidebar = width + rightOffset + rightSidebarOffset;
	/**
	 * Preview panel also opens inside RightPanel and is resizable. And it has default width of 400px,
	 * which is more than width of RightSidebar (386px)
	 */
	// Preview panel takes precedence over sidebar when open
	if (fg('jira-glance-panel')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const panel = useCurrentPreviewPanel();
		if (panel) {
			distanceFromRightSidebar = panel.width + rightOffset;
		}
	}

	return (
		<StyledContainer
			ref={registerBottomRightCornerContainer}
			data-testid="layout-controller.ui.bottom-right-corner.container.styled-container"
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				right: `calc(${distanceFromRightSidebar}px + ${token('space.300')})`,
				bottom: `calc(${token('space.300')} + ${bottomOffset}px)`,
			}}
		/>
	);
}
export const GlobalBottomRightCornerContainer = ({
	bottomOffset = 0,
	rightOffset = 0,
}: GlobalBottomRightCornerContainerProps) => (
	<ErrorBoundary
		id="bottom-right-corner-container"
		packageName="jira-bottom-right-corner-controller"
	>
		<ContainerWithRef bottomOffset={bottomOffset} rightOffset={rightOffset} />
	</ErrorBoundary>
);
export const GlobalBottomRightCornerContainerProvider = ({ children }: ProviderProps) => {
	const container = useBottomRightCornerContainer();
	return (
		<UFOSegment name="global-bottom-right-corner">
			{container && children}
			<GlobalBottomRightCornerContainer />
		</UFOSegment>
	);
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	position: 'fixed',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.navigation - 1,
	overflow: 'visible',
	alignItems: 'flex-end',
	pointerEvents: 'none',
});
StyledContainer.displayName = 'StyledContainer';
